import { isNull } from 'lodash';
import { useEffect, useRef } from 'react';
import { ACDLError } from '../../__legacy__/models/error.model';
import { IModelImageDto } from '../../__legacy__/modules/annotation/annotations.interface';
import { AnnotationToolType } from '../../__legacy__/modules/annotation/submodules/annotationTypes/models/annotationToolType';
import { IAnnotationType } from '../../__legacy__/modules/projectDetails/sub/tools/projectDetailsTools.models';
import { Home } from '../../__legacy__/routes/config/Home';
import { InferencesPath } from '../../__legacy__/routes/config/Inferences';
import { TrainingsPath } from '../../__legacy__/routes/config/Trainings';
import { IJobDetailsStore } from './jobDetails.store';
import { IPredictionImage } from './jobPredictions.model';
import { IJobImagePredictionDto } from './jobs.api.model';
import { INFERENCE_TYPE, JOB_TYPE } from './jobs.model';

export function getJobRoutePrefix(jobType: JOB_TYPE.TRAINING): TrainingsPath;
export function getJobRoutePrefix(jobType: JOB_TYPE.INFERENCE): InferencesPath;
export function getJobRoutePrefix(jobType: JOB_TYPE): TrainingsPath | InferencesPath;
export function getJobRoutePrefix(jobType: JOB_TYPE): TrainingsPath | InferencesPath {
  if (jobType === JOB_TYPE.UNKNOWN) {
    throw new ACDLError('Invalid jobType');
  }

  return jobType === JOB_TYPE.TRAINING ? Home.Trainings : Home.Inferences;
}

export const getJobTranslationPrefix = (jobType: JOB_TYPE): 'training' | 'inference' => {
  switch (jobType) {
    case JOB_TYPE.INFERENCE:
      return 'inference';
    case JOB_TYPE.UNKNOWN:
    case JOB_TYPE.TRAINING:
    default:
      return 'training';
  }
};

type EffectCallback = () => unknown;

export const useDebouncedEffect = (effect: EffectCallback, deps: unknown[], delay: number): void => {
  const callback = useRef<EffectCallback>(null);
  const timeout = useRef<ReturnType<typeof setTimeout>>(null);

  // Remember the latest effect function.
  useEffect(() => {
    callback.current = effect;
  }, [effect]);

  useEffect(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }

    timeout.current = setTimeout(() => {
      if (callback.current) {
        callback.current();
      }
    }, delay);

    return (): void => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, [...deps, delay]);
};
export class AsyncGuardError extends ACDLError {
  constructor() {
    super('Operation called by previous version of the service.');
    Object.setPrototypeOf(this, AsyncGuardError.prototype);
  }
}

export const ensureNotStale = async <T>(method: () => Promise<T>, identifierGetter: () => string): Promise<T | ACDLError> => {
  const previousIdentifier = identifierGetter();
  const result = await method();
  const identifier = identifierGetter();

  if (previousIdentifier !== identifier) {
    return new AsyncGuardError();
  }

  return result;
};

export function assertNotNull<T>(value: T | null, message: string): asserts value is T {
  if (value === null) {
    throw new ACDLError(message);
  }
}

export const buildPredictionImage = (
  image: IPredictionImage | IModelImageDto,
  job: IJobDetailsStore['job'],
  predictionData: IJobImagePredictionDto,
  heatmapUrl: string | null,
  maskUrl: string | null,
  annotationTypes: IAnnotationType[]
): IPredictionImage => {
  const hasProject = job.projectId !== null;
  const { ground_truth, metrics, prediction, predicted_points, predicted_rectangles } = predictionData;
  const mappedGroundTruth = ground_truth
    ? {
        className: ground_truth.class_name,
        predictionCounts: ground_truth.prediction_counts,
      }
    : null;
  return {
    id: image.id,
    name: image.name,
    url: 'url' in image ? image.url : '',
    imageSetType: image.imageSetType,
    prediction: {
      // Set ground_truth to undefined for evaluations on dataset, which will cause "Annotated as" to not be displayed
      groundTruth: job.type === INFERENCE_TYPE.DATASET || !hasProject ? undefined : mappedGroundTruth,
      prediction: prediction ?? null,
      confusionMatrix: metrics.confusion_matrix ?? null,
      recall: metrics.recall ?? null,
      precision: metrics.precision ?? null,
      f1: metrics.f1 ?? null,
      auc: metrics.auc ?? null,
      count: metrics.count ?? null,
      precisionRecallCurve: metrics.precision_recall_curve ?? null,
      score: metrics.score ?? null,
      averageEntropy: metrics.average_entropy ?? null,
      confidence: metrics.confidence ?? null,
    },
    heatmapUrl,
    maskUrl,
    predictedPoints: !predicted_points
      ? null
      : predicted_points
          .map((prediction) => {
            const annotationType = annotationTypes.find((annotationType) => annotationType.id === prediction.class_id);
            if (!annotationType) {
              return null;
            }

            return {
              selectorType: AnnotationToolType.POINT,
              classId: prediction.class_id,
              className: annotationType.name,
              color: annotationType.color,
              points: prediction.points,
            };
          })
          .filter((prediction) => !isNull(prediction)),
    predictedRectangles: !predicted_rectangles
      ? null
      : predicted_rectangles
          .map((prediction) => {
            const annotationType = annotationTypes.find((annotationType) => annotationType.id === prediction.class_id);
            if (!annotationType) {
              return null;
            }

            return {
              selectorType: annotationType.selectorType,
              classId: prediction.class_id,
              className: annotationType.name,
              color: annotationType.color,
              rectangles: prediction.rectangles,
            };
          })
          .filter((prediction) => !isNull(prediction)),
  };
};
